/**
 * Rules of the players' chat: the channels, the text of a message (plain text only) and the word filter. ONE module,
 * browser-safe (no Node, DOM or protocol imports), like ./clan-rules.ts: the server applies it authoritatively
 * (src/server/core/services/chat.ts) and a client of any platform uses the same functions for its character counter
 * and its instant checks (system.hello `limits.chat` publishes the numbers). The reasons are the stable
 * `details.reason` values of the protocol's errors (src/shared/protocol/reasons.ts).
 *
 * Channels
 *  - `global`: every signed-in player reads it; registered players (not guests) of at least limits.chat.minLevelToSend
 *    write to it;
 *  - `clan:<clanId>`: the members of that clan only (checked on every read, subscription, delivery and send: a player
 *    who left the clan or was removed from it reads nothing more of it).
 *
 * Text (sanitizeChatText; what the server stores and every client shows, always as plain text, never as markup)
 *  - lone UTF-16 surrogates (half of an emoji) are dropped, the text is NFC-normalized;
 *  - line breaks and tabs become spaces; control and format characters (zero-width, bidirectional overrides, the
 *    invisible "filler" letters) are removed, except a zero-width joiner inside an emoji sequence; private-use and
 *    unassigned code points are removed;
 *  - anything that looks like an HTML / XML tag (`<b>`, `</font>`, `<!-- -->`, `<?xml ?>`) is removed, so a client that
 *    shows text in a field that understands markup (a Flash htmlText) shows the words, never the markup;
 *  - at most CHAT_MAX_MARKS combining marks in a row (no "zalgo" text that covers the lines above and below), at most
 *    CHAT_MAX_REPEAT equal characters in a row;
 *  - white space runs become one space, both ends are trimmed;
 *  - then 1..CHAT_TEXT_MAX characters (code points), else chat_text_empty / chat_text_too_long.
 * Word filter (maskChatWords): the operator's words and phrases (CHAT_WORD_FILTER_FILE) are masked with `*`, never
 * refused; the comparison ignores case, accents and other combining marks, fullwidth and "mathematical" letters and the
 * Cyrillic and Greek letters that look like Latin ones (a word typed with lookalikes is masked too); a word matches
 * anywhere in the text (roots of words), a phrase with its single spaces.
 */

export const CHAT_GLOBAL = 'global';
/** A channel id: `global` or `clan:<clanId>`. */
export type ChatChannelId = string;
export const CHAT_CHANNEL_PATTERN = /^(?:global|clan:[1-9][0-9]{0,9})$/;
/** Longest message after sanitization (code points). */
export const CHAT_TEXT_MAX = 200;
/**
 * Bound of the request payload schema (UTF-16 units): generous, so that the service answers a too long text with its
 * specific reason; anything longer is a malformed payload (BAD_REQUEST 'invalid_payload').
 */
export const CHAT_TEXT_INPUT_MAX = 2000;
/** Combining marks kept in a row, and equal characters kept in a row. */
export const CHAT_MAX_MARKS = 3;
export const CHAT_MAX_REPEAT = 10;

export type ChatTextProblem = 'chat_text_empty' | 'chat_text_too_long';
export type ChatTextCheck = { ok: true; text: string } | { ok: false; reason: ChatTextProblem; max: number };

export type ParsedChatChannel = { kind: 'global' } | { kind: 'clan'; clanId: number };

/** The channel of a clan. */
export function clanChannel(clanId: number): ChatChannelId {
  return `clan:${clanId}`;
}

/** What a channel id names, or null when it is not one. */
export function parseChatChannel(channel: unknown): ParsedChatChannel | null {
  if (typeof channel !== 'string' || !CHAT_CHANNEL_PATTERN.test(channel)) return null;
  if (channel === CHAT_GLOBAL) return { kind: 'global' };
  const clanId = Number(channel.slice(5));
  return Number.isSafeInteger(clanId) && clanId > 0 && clanId <= 2_147_483_647 ? { kind: 'clan', clanId } : null;
}

/** Characters of a text (code points: an emoji of two UTF-16 units is one). */
export function chatTextLength(text: string): number {
  let n = 0;
  for (const _ of text) n++;
  return n;
}

const LONE_SURROGATE = /\p{Cs}/gu;
const LINE_BREAKS = /[\t\n\v\f\r\u0085\u2028\u2029]/g;
/** Control, format (but the zero-width joiner), private-use and unassigned code points. */
const CONTROL_FORMAT = /[\p{Cc}\p{Co}\p{Cn}]|(?!\u200d)\p{Cf}/gu;
/** A zero-width joiner that does not join two pictographs (an emoji sequence keeps its joiners). */
const STRAY_JOINER = /(?<!\p{Extended_Pictographic}\ufe0f?)\u200d|\u200d(?!\p{Extended_Pictographic})/gu;
/** Letters that show nothing: blank "names" and empty-looking messages (Hangul fillers, the braille blank). */
const INVISIBLE_LETTERS = /[\u115f\u1160\u3164\uffa0\u2800\u17b4\u17b5]/g;
/** Tag-like markup: `<b>`, `</font>`, `<a href=...>`, `<!-- -->`, `<?xml ?>`. */
const MARKUP = /<\s*[/!?]?\s*[A-Za-z!?-][^<>]*>/g;
const MARK_RUN = new RegExp(`(\\p{M}{${CHAT_MAX_MARKS}})\\p{M}+`, 'gu');
const REPEAT_RUN = new RegExp(`(.)\\1{${CHAT_MAX_REPEAT},}`, 'gsu');

/** The plain text of a message as the server stores it (see the module comment); may be empty. */
export function sanitizeChatText(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  let s = raw.replace(LONE_SURROGATE, '').normalize('NFC');
  s = s.replace(LINE_BREAKS, ' ').replace(CONTROL_FORMAT, '').replace(STRAY_JOINER, '').replace(INVISIBLE_LETTERS, '');
  // Markup removed until none is left (a tag split by another tag: "<<b>b>").
  for (let i = 0; i < 8 && MARKUP.test(s); i++) {
    MARKUP.lastIndex = 0;
    s = s.replace(MARKUP, '');
  }
  MARKUP.lastIndex = 0;
  s = s.replace(MARK_RUN, '$1').replace(REPEAT_RUN, (run: string, c: string) => c.repeat(CHAT_MAX_REPEAT));
  return s.replace(/\s+/gu, ' ').trim();
}

/** Checks a message's text (sanitized first); on success the text to store. */
export function checkChatText(raw: unknown, max = CHAT_TEXT_MAX): ChatTextCheck {
  const text = sanitizeChatText(raw);
  if (text === '') return { ok: false, reason: 'chat_text_empty', max };
  if (chatTextLength(text) > max) return { ok: false, reason: 'chat_text_too_long', max };
  return { ok: true, text };
}

/** Cyrillic and Greek letters that look like Latin ones -> the Latin twin (lower case; folded after NFKD, so "ё" is "е"). */
const LOOKALIKES: ReadonlyMap<string, string> = new Map([
  ['а', 'a'],
  ['в', 'b'],
  ['с', 'c'],
  ['е', 'e'],
  ['ё', 'e'],
  ['н', 'h'],
  ['і', 'i'],
  ['ї', 'i'],
  ['\u0458', 'j'],
  ['к', 'k'],
  ['м', 'm'],
  ['о', 'o'],
  ['р', 'p'],
  ['\u0455', 's'],
  ['т', 't'],
  ['х', 'x'],
  ['у', 'y'],
  ['0', 'o'],
  // Greek
  ['α', 'a'],
  ['β', 'b'],
  ['ε', 'e'],
  ['ι', 'i'],
  ['κ', 'k'],
  ['ν', 'v'],
  ['ο', 'o'],
  ['ρ', 'p'],
  ['τ', 't'],
  ['υ', 'u'],
  ['χ', 'x'],
]);

/**
 * The comparison form of one code point (may be empty or longer than one character): its compatibility decomposition
 * without the combining marks (accents, fullwidth and "mathematical" letters, ligatures fold to plain letters: "ü",
 * "ｕ", "𝐮" -> "u"), lower case, the lookalike letters replaced by their Latin twins.
 */
function foldChar(c: string): string {
  const base = c.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
  let out = '';
  for (const x of base) out += LOOKALIKES.get(x) ?? x;
  return out;
}

/** A filter word as compared: sanitized, folded, white space runs as one space (a phrase), trimmed (empty: no word). */
function foldWord(word: string): string {
  return Array.from(sanitizeChatText(word), foldChar)
    .join('')
    .replace(/\s+/gu, ' ')
    .trim();
}

/** The folded keys of a word list (one list is the server's configuration: folded once). */
const foldedLists = new WeakMap<readonly string[], string[][]>();
function filterKeys(words: readonly string[]): string[][] {
  let keys = foldedLists.get(words);
  if (!keys) {
    keys = [...new Set(words.map(foldWord))].map((w) => Array.from(w)).filter((w) => w.length >= 2);
    foldedLists.set(words, keys);
  }
  return keys;
}

/**
 * The text with every occurrence of the filter's words masked by `*` (one per character), and whether anything was
 * masked. Words of fewer than 2 characters are ignored (they would mask parts of every other word). The comparison
 * folds every character (foldChar: case, accents, fullwidth and lookalike letters do not matter) and skips the
 * combining marks between letters ("b\u0301adword" is masked too, with its marks).
 */
export function maskChatWords(text: string, words: readonly string[]): { text: string; masked: boolean } {
  const keys = filterKeys(words);
  if (keys.length === 0 || text === '') return { text, masked: false };
  const chars = Array.from(text);
  // The folded text, each folded character with the index of the text's character it comes from.
  const folded: string[] = [];
  const from: number[] = [];
  /** Characters that fold to nothing (combining marks): masked with the letter before them. */
  const blank: boolean[] = [];
  chars.forEach((c, i) => {
    const f = foldChar(c);
    blank.push(f === '');
    for (const x of f) {
      folded.push(x);
      from.push(i);
    }
  });
  const hide = new Array<boolean>(chars.length).fill(false);
  for (const k of keys) {
    for (let i = 0; i + k.length <= folded.length; i++) {
      let j = 0;
      while (j < k.length && folded[i + j] === k[j]) j++;
      if (j !== k.length) continue;
      let last = from[i + k.length - 1];
      while (last + 1 < chars.length && blank[last + 1]) last++;
      for (let x = from[i]; x <= last; x++) hide[x] = true;
    }
  }
  if (!hide.includes(true)) return { text, masked: false };
  return { text: chars.map((c, i) => (hide[i] && !/\s/u.test(c) ? '*' : c)).join(''), masked: true };
}

/** The key of a text for the flood check (case, lookalikes, spaces and punctuation do not matter). */
export function chatDuplicateKey(text: string): string {
  return Array.from(text, foldChar)
    .join('')
    .replace(/[^\p{L}\p{N}\p{So}]/gu, '');
}
